/**
 * Lake Effect Lures — page chrome behaviour.
 * Announcement bar rotator, header mega-menu + mobile drawer, hero rotator,
 * cart-count refresh. Vanilla ES2019+, no build step, no framework.
 *
 * Progressive enhancement: everything this file controls is either present
 * and usable without JS (first announcement item, first hero slide, the
 * full desktop nav) or is gated behind the `le-js` class this file adds to
 * <html>, so nothing relies on JS for baseline content.
 *
 * Custom events:
 *  - listens for `le:cart:updated` on `document` and refetches /cart.js to
 *    update every [data-le-cart-count].
 */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('le-js'); // no-op when theme.liquid's inline script already ran

  // The header can grow to a second row at desktop widths. Keep fixed chrome
  // offsets accurate instead of assuming the single-row token height.
  function syncChromeHeight() {
    var chrome = document.querySelector('.le-site-chrome');
    if (chrome) root.style.setProperty('--le-chrome-height', Math.ceil(chrome.getBoundingClientRect().height) + 'px');
  }
  syncChromeHeight();
  if ('ResizeObserver' in window) {
    var chrome = document.querySelector('.le-site-chrome');
    if (chrome) new ResizeObserver(syncChromeHeight).observe(chrome);
  } else {
    on(window, 'resize', syncChromeHeight);
  }

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -----------------------------------------------------------------------
     Small helpers
     ----------------------------------------------------------------------- */
  function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /**
   * A generic "rotator": cycles through `items` (elements), showing exactly
   * one at a time via the `hidden` attribute or an `is-active` class,
   * auto-advancing on an interval unless reduced motion is requested,
   * pausing on hover/focus and while offscreen.
   */
  function createRotator(opts) {
    var items = opts.items;
    var dwell = opts.dwell;
    var container = opts.container;
    var autoplay = opts.autoplay && items.length > 1 && !reduceMotion;
    var onChange = opts.onChange || function () {};
    var index = opts.startIndex || 0;
    var timer = null;
    var paused = false;
    var visible = true;

    function show(i) {
      index = (i + items.length) % items.length;
      onChange(index, items[index]);
    }

    function next() { show(index + 1); }
    function prev() { show(index - 1); }

    function tick() {
      if (!paused && visible && autoplay) next();
    }

    function start() {
      if (!autoplay || timer) return;
      timer = window.setInterval(tick, dwell);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    if (container) {
      on(container, 'mouseenter', function () { paused = true; });
      on(container, 'mouseleave', function () { paused = false; });
      on(container, 'focusin', function () { paused = true; });
      on(container, 'focusout', function () { paused = false; });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
          });
        }, { threshold: 0.1 });
        io.observe(container);
      }
    }

    show(index);
    start();

    return { next: next, prev: prev, goTo: show, stop: stop, start: start };
  }

  /* -----------------------------------------------------------------------
     Announcement bar — data-le-announce
     ----------------------------------------------------------------------- */
  qsa(document, '[data-le-announce]').forEach(function (bar) {
    var items = qsa(bar, '[data-le-announce-item]');
    if (!items.length) return;
    var dwell = parseInt(bar.getAttribute('data-dwell'), 10) || 5000;
    var autoplay = bar.getAttribute('data-autoplay') !== 'false';
    var prevBtn = bar.querySelector('[data-le-announce-prev]');
    var nextBtn = bar.querySelector('[data-le-announce-next]');

    var rotator = createRotator({
      items: items,
      dwell: dwell,
      container: bar,
      autoplay: autoplay,
      onChange: function (i) {
        items.forEach(function (el, idx) {
          if (idx === i) {
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
      }
    });

    on(prevBtn, 'click', rotator.prev);
    on(nextBtn, 'click', rotator.next);
  });

  /* -----------------------------------------------------------------------
     Hero rotator — data-le-hero
     ----------------------------------------------------------------------- */
  qsa(document, '[data-le-hero]').forEach(function (hero) {
    var slides = qsa(hero, '[data-le-hero-slide]');
    if (!slides.length) return;
    var dwell = parseInt(hero.getAttribute('data-dwell'), 10) || 6000;
    var autoplay = hero.getAttribute('data-autoplay') !== 'false';
    var dots = qsa(hero, '[data-le-hero-dot]');
    var prevBtn = hero.querySelector('[data-le-hero-prev]');
    var nextBtn = hero.querySelector('[data-le-hero-next]');

    var rotator = createRotator({
      items: slides,
      dwell: dwell,
      container: hero,
      autoplay: autoplay,
      onChange: function (i) {
        slides.forEach(function (el, idx) {
          el.classList.toggle('is-active', idx === i);
        });
        dots.forEach(function (el, idx) {
          var active = idx === i;
          el.classList.toggle('is-active', active);
          el.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    });

    on(prevBtn, 'click', rotator.prev);
    on(nextBtn, 'click', rotator.next);
    dots.forEach(function (dot, i) {
      on(dot, 'click', function () { rotator.goTo(i); });
    });
  });

  /* -----------------------------------------------------------------------
     Header — mega menu (hover handled in CSS; this adds click + keyboard)
     ----------------------------------------------------------------------- */
  qsa(document, '[data-le-mega-trigger]').forEach(function (trigger) {
    var wrap = trigger.closest('.le-header__nav-item');
    var panelId = trigger.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!wrap || !panel) return;
    var closeTimer = null;
    var openedByPointer = false;

    function clearCloseTimer() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function closeMenu() {
      clearCloseTimer();
      openedByPointer = false;
      wrap.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    }

    function openMenu() {
      clearCloseTimer();
      wrap.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    }

    function scheduleClose() {
      clearCloseTimer();
      closeTimer = window.setTimeout(closeMenu, 180);
    }

    // Keep the open state alive briefly while the pointer travels from a
    // trigger toward a submenu. CSS still provides the no-JS hover fallback.
    on(wrap, 'pointerenter', function (e) {
      if (e.pointerType === 'touch') return;
      openedByPointer = true;
      openMenu();
    });
    on(wrap, 'pointerleave', function (e) {
      if (e.pointerType === 'touch') return;
      openedByPointer = false;
      scheduleClose();
    });

    on(trigger, 'click', function (e) {
      e.preventDefault();
      var isOpen = wrap.classList.contains('is-open');
      // The first click after a pointer hover should keep the menu open;
      // subsequent clicks still provide the expected toggle behavior.
      if (isOpen && openedByPointer) {
        openedByPointer = false;
        return;
      }
      // Close any sibling menus first.
      qsa(document, '.le-header__nav-item.is-open').forEach(function (openWrap) {
        if (openWrap !== wrap) {
          openWrap.classList.remove('is-open');
          var t = openWrap.querySelector('[data-le-mega-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    on(trigger, 'keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openMenu();
        var firstLink = panel.querySelector('a');
        if (firstLink) firstLink.focus();
      }
    });

    on(panel, 'keydown', function (e) {
      var links = qsa(panel, 'a');
      var i = links.indexOf(document.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        trigger.focus();
      } else if (e.key === 'ArrowDown' && i > -1) {
        e.preventDefault();
        var nextLink = links[i + 1] || links[0];
        nextLink.focus();
      } else if (e.key === 'ArrowUp' && i > -1) {
        e.preventDefault();
        var prevLink = links[i - 1] || links[links.length - 1];
        prevLink.focus();
      }
    });

    on(panel, 'focusin', function () {
      openMenu();
    });

    on(wrap, 'keydown', function (e) {
      if (e.key === 'Escape') {
        closeMenu();
        trigger.focus();
      }
    });

    on(wrap, 'focusout', function (e) {
      window.setTimeout(function () {
        if (!wrap.contains(document.activeElement)) closeMenu();
      }, 0);
    });
  });

  document.addEventListener('click', function (e) {
    qsa(document, '.le-header__nav-item.is-open').forEach(function (wrap) {
      if (!wrap.contains(e.target)) {
        wrap.classList.remove('is-open');
        var t = wrap.querySelector('[data-le-mega-trigger]');
        if (t) t.setAttribute('aria-expanded', 'false');
      }
    });
  });

  /* -----------------------------------------------------------------------
     Header — mobile drawer
     ----------------------------------------------------------------------- */
  (function () {
    var trigger = document.querySelector('[data-le-drawer-trigger]');
    var drawer = document.querySelector('[data-le-drawer]');
    if (!trigger || !drawer) return;
    var drawerAnimationMs = 160;

    function closeDrawer() {
      drawer.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
      window.setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, drawerAnimationMs);
    }

    function openDrawer() {
      drawer.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(function () {
        drawer.classList.add('is-open');
      });
      var closeBtn = drawer.querySelector('[data-le-drawer-close]');
      if (closeBtn) closeBtn.focus();
    }

    on(trigger, 'click', function () {
      if (drawer.hidden) openDrawer(); else closeDrawer();
    });

    qsa(drawer, '[data-le-drawer-close]').forEach(function (el) {
      on(el, 'click', closeDrawer);
    });

    on(drawer, 'keydown', function (e) {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }

      if (e.key === 'Tab') {
        var focusable = qsa(drawer, 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    qsa(drawer, '[data-le-drawer-subtrigger]').forEach(function (sub) {
      var subPanelId = sub.getAttribute('aria-controls');
      var subPanel = subPanelId ? document.getElementById(subPanelId) : null;
      if (!subPanel) return;
      on(sub, 'click', function () {
        var open = sub.getAttribute('aria-expanded') === 'true';
        sub.setAttribute('aria-expanded', open ? 'false' : 'true');
        subPanel.hidden = open;
      });
    });
  })();

  /* -----------------------------------------------------------------------
     Cart count refresh
     ----------------------------------------------------------------------- */
  function setCartCount(count) {
    qsa(document, '[data-le-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  document.addEventListener('le:cart:updated', function () {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) { setCartCount(cart.item_count); })
      .catch(function () {});
  });
})();
